<style scoped>

  .ui.segment.top-segment {
    min-height: 242px;
  }

</style>

<template>
  
  <div class="ui grid animated fadeIn">

    <div class="eight wide computer sixteen wide tablet sixteen wide mobile column">
      <div ref="certDataSegment" class="ui segment top-segment">
        <h4 class="ui dividing header">
          Dados do Laudo
          <span v-if="finalityLabel" class="ui label">{{ finalityLabel }}</span>
          <span v-if="cert.customerInvoiceId" class="ui teal label clickable" @click="onShowCustomerInvoice()">
            Fechamento <div class="detail">{{ cert.customerInvoiceId }}</div>
          </span>
        </h4>
        <div class="ui form">           
          <div class="fields">
            <div class="two wide field">
              <label class="form-label">Número</label>
              {{ cert.id }}
            </div>
            <div class="three wide field">
              <label class="form-label">Data</label>
              {{ $formatDate(cert.date) }}
            </div>
            <div class="four wide field">
              <label class="form-label">Status</label>
              <cert-status :status="cert.status" />
            </div>                                        
            <div class="seven wide field">
              <label class="form-label">Serviço</label>
              <div>
                {{ cert.service.description }}
                <div class="ui small label m-l-5">
                  <service-type-icon :icon="cert.serviceType.icon" />
                  {{ cert.serviceType.description }}
                </div>
              </div>
            </div>
          </div>          
          <div class="fields">
            <div class="sixteen wide field">
              <label class="form-label">Colaborador</label>
              {{ cert.user.name.toUpperCase() + ' ' + cert.user.lastName.toUpperCase() }}
            </div>                             
          </div>          
          <div class="fields">
            <div class="eight wide field">
              <label class="form-label">Veterinário</label>
              {{ cert.vet.name }}
            </div>   
          </div>
        </div>
      </div>
    </div>

    <div class="eight wide computer sixteen wide tablet sixteen wide mobile column">
      <div ref="customerSegment" class="ui segment top-segment">
        <h4 class="ui dividing header">Clínica</h4>
        <div class="ui form">           
          <div class="fields">
            <div class="five wide field">
              <label class="form-label">Pessoa</label>              
              <span v-if="cert.customer.person == 'F'"><i class="user outline blue icon"></i> Física</span>
              <span v-else><i class="building outline red icon"></i> Jurídica</span>
            </div>
            <div class="eleven wide field">
              <label class="form-label">Nome</label>
              {{ cert.customer.shortName }}
            </div>            
          </div> 
          <div class="fields">            
            <div class="five wide field">
              <label class="form-label">{{ cert.customer.person == 'F' ? 'CPF' : 'CNPJ' }}</label>
              {{ cert.customer.cgc ? $formatCgc(cert.customer.cgc) : "Não cadastrado" }}
            </div>     
            <div class="eleven wide field">
              <label class="form-label">Endereço</label>
              {{ customerAddress }}
            </div>
          </div>
          <div class="fields">            
            <div class="five wide field">
              <label class="form-label">Telefone</label>
              {{ cert.customer.phone || "Não cadastrado" }}
            </div>
            <div class="eleven wide field">
              <label class="form-label">E-mail</label>
              {{ cert.customer.email || "Não cadastrado" }}
            </div>
          </div>                                                 
        </div>
      </div>
    </div>

    <div v-if="$isSimavet() && cert.examRequestId" class="sixteen wide computer sixteen wide tablet sixteen wide mobile column">
      <div class="ui segment">        
        <h4 class="ui dividing header">Solicitação de Exame</h4> 
        <router-link class="ui mini basic button m-b-20" :to="`/procs/exam-requests/${cert.examRequest.id}/show`">
          <i class="search plus icon"></i> Visualizar
        </router-link>       
        <div class="ui form">
          <div class="fields">
            <div class="two wide field">
              <label class="form-label">Número</label>
              {{ cert.examRequest.id }}
            </div> 
            <div class="two wide field">
              <label class="form-label">Data</label>
              {{ $formatDate(cert.examRequest.date) }}
            </div>
            <div class="three wide field">
              <label class="form-label">Responsável</label>
              {{ cert.examRequest.user.name }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Veterinário</label>
              {{ cert.examRequest.vetName }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Clínica</label>
              {{ cert.examRequest.customerName }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Animal</label>
              {{ cert.examRequest.animalName }}              
            </div>      
          </div>
          <div class="fields">               
            <div class="two wide field">
              <label class="form-label">Sexo</label>
              <span v-if="cert.examRequest.animalSex == 'male'"><i class="mars blue icon"></i> Macho</span>
              <span v-if="cert.examRequest.animalSex == 'female'"><i class="venus pink icon"></i> Fêmea</span>
              <span v-if="cert.examRequest.animalSex == 'undefined'"><i class="question circle grey icon"></i> Indefinido</span>
            </div>                            
            <div class="two wide field">
              <label class="form-label">Raça</label>
              {{ cert.examRequest.raceName }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Espécie</label>
              {{ cert.examRequest.speciesName }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Idade</label>
              {{ cert.examRequest.animalAge }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Tutor</label>
              {{ cert.examRequest.ownerName }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Tipo do Exame</label>
              {{ cert.examType.description }}
            </div> 
          </div>
          <div class="fields">                
            <div class="sixteen wide field">
              <label class="form-label m-b-10">Suspeita Clínica</label>
              <span v-html="suspicion"></span>
            </div>
          </div>
        </div>    
      </div>
    </div>  

    <div class="sixteen wide computer sixteen wide tablet sixteen wide mobile column">
      <div class="ui segment">
        <h4 class="ui dividing header">Animal</h4>        
        <button class="ui mini basic button m-b-20" @click="$refs.animalPicturesModal.show(cert.animal.id)">
          <i class="camera violet icon"></i> Fotos
        </button>     
        <router-link v-show="cert.animalId" class="ui mini basic button m-l-10" :to="`/entries/animal-registrations/${cert.animalRegistrationId}?historyBack=true`">
          <i class="search teal icon"></i> Visualizar Ficha
        </router-link>                
        <div class="ui form">
          <div class="fields">
            <div class="three wide field">
              <label class="form-label">Clínica</label>
              {{ cert.customer.shortName }}
            </div>
            <div class="two wide field">
              <label class="form-label">Ficha Cadastral</label>
              {{ cert.animalRegistration.registrationId }}
            </div>                  
            <div class="three wide field">
              <label class="form-label">Nome do Animal</label>
              {{ cert.animal.name }}
              <i v-if="cert.animal.dead" class="cross grey icon m-l-5"></i>
            </div>
            <div class="two wide field">
              <label class="form-label">Sexo</label>
              <span v-if="cert.animal.sex == 'male'"><i class="mars blue icon"></i> Macho</span>
              <span v-if="cert.animal.sex == 'female'"><i class="venus pink icon"></i> Fêmea</span>
              <span v-if="cert.animal.sex == 'undefined'"><i class="question circle grey icon"></i> Indefinido</span>
            </div>              
            <div class="three wide field">
              <label class="form-label">Raça</label>
              {{ cert.animal.race.name }}
            </div>
            <div class="three wide field">
              <label class="form-label">Espécie</label>
              {{ cert.animal.race.species.name }}
            </div>
          </div>
          <div class="fields">
            <div v-if="cert.animal.birthDate" class="three wide field">
              <label class="form-label">Idade</label>
              {{ animalAge }}
            </div>
            <div v-else class="three wide field">
              <label class="form-label">Faixa Etária</label>
              {{ animalAge }}
            </div>
            <div class="two wide field">
              <label class="form-label">Peso</label>
              {{ cert.animal.weight ? $formatCurrency(cert.animal.weight) + " KG" : "Não cadastrado" }}
            </div> 
            <div class="three wide field">
              <label class="form-label">Tutor</label>
              {{ cert.animal.owner.name }}
            </div>
            <div class="two wide field">
              <label class="form-label">Telefone</label>
              {{ cert.animal.owner.phone || "Não cadastrado" }}
            </div>
            <div class="three wide field">
              <label class="form-label">Celular</label>
              {{ cert.animal.owner.mobile || "Não cadastrado" }}
            </div>
            <div class="three wide field">
              <label class="form-label">E-mail</label>
              {{ cert.animal.owner.email || "Não cadastrado" }}
            </div>
          </div>            
        </div>    
      </div>
    </div>  

    <div v-if="$isSimavet() && cert.complementRequest" class="sixteen wide computer sixteen wide tablet sixteen wide mobile column">
      <div class="ui segment">        
        <h4 class="ui dividing header">Solicitação de Complemento</h4>    
        <button class="ui mini basic button m-b-20" :class="{ loading: removingComplement }" 
          @click="$refs.confirmComplementRemovalModal.show()">
          <i class="times red icon"></i> Excluir
        </button>    
        <div class="ui form">
          <div class="fields">
            <div class="two wide field">
              <label class="form-label">Número</label>
              {{ cert.complementRequest.id }}
            </div> 
            <div class="two wide field">
              <label class="form-label">Status</label>
              <cert-complement-request-status :status="cert.complementRequest.status" />
            </div>
            <div class="two wide field">
              <label class="form-label">Data</label>
              {{ $formatDate(cert.complementRequest.createdAt) }}
            </div>
            <div class="two wide field">
              <label class="form-label">Hora</label>
              {{ $formatTime(cert.complementRequest.createdAt) }}
            </div>
            <div class="four wide field">
              <label class="form-label">Solicitante</label>
              {{ cert.complementRequest.requester.name + ' ' + cert.complementRequest.requester.lastName }}
            </div>          
            <div class="four wide field">
              <label class="form-label">Responsável</label>
              {{ cert.complementRequest.responsible.name + ' ' + cert.complementRequest.responsible.lastName }}
            </div>                 
          </div>          
          <div v-if="complementRequesterComments || complementResponsibleComments" class="fields">                
            <div v-if="complementRequesterComments" class="eight wide field">
              <label class="form-label m-b-10">Observações do Solicitante</label>
              <span v-html="complementRequesterComments"></span>
            </div>
            <div v-if="complementResponsibleComments" class="eight wide field">
              <label class="form-label m-b-10">Observações do Responsável</label>
              <span v-html="complementResponsibleComments"></span>
            </div>
          </div>            
        </div>    
      </div>
    </div>  
    
    <animal-pictures-modal ref="animalPicturesModal" />
    
    <yes-no-modal ref="confirmComplementRemovalModal" :message="confirmComplementRemovalLabel" 
      :danger="true" @onYes="onRemoveComplement" />

  </div>

</template>

<script>
  
  import CertService from "@/services/cert-service"
  import AnimalService from "@/services/animal-service"
  import AnimalPicturesModal from "@/pages/entries/animals/pictures-modal"
  import { CertStatus, ServiceTypeIcon, CertComplementRequestStatus, YesNoModal } from "@/components"
  import { AppBrowser, moment, replaceAll } from "@/utils"

  export default {
    
    name: "CertOverviewTab",

    data() {
      return {
        removingComplement: false,
        service: new CertService(),
      }
    },

    props: ["cert"],

    components: {
      AnimalPicturesModal,
      CertStatus, 
      ServiceTypeIcon,
      CertComplementRequestStatus,
      YesNoModal,
    },

    mounted() {
      if (this.$isDesktop)
        this.$refs.customerSegment.style.height = (this.$refs.certDataSegment.clientHeight + 1) + "px"
    },

    computed: {

      finalityLabel() {

        let label = ""

        if (this.cert.finality == "control") {
          label = "Controle"
        } else if (this.cert.finality == "revaluation") {
          label = "Reavaliação"
        }

        return label
      },

      suspicion() {

        if (!this.cert.examRequest.suspicion)
          return ""

        return replaceAll(this.cert.examRequest.suspicion, "\n", "<br/>")  
      },

      complementRequesterComments() {

        if (!this.cert.complementRequest || !this.cert.complementRequest.comments)
          return ""

        return replaceAll(this.cert.complementRequest.comments, "\n", "<br/>")  
      },

      complementResponsibleComments() {

        if (!this.cert.complementRequest || !this.cert.complementRequest.responsibleComments)
          return ""

        return replaceAll(this.cert.complementRequest.responsibleComments, "\n", "<br/>")  
      },

      animalAge() {

        if (!this.cert.animal)
          return ""

        return this.cert.animal.birthDate ? moment.formatAge(this.cert.animal.birthDate) : this.getAgeRange() 
      },      

      confirmComplementRemovalLabel() {
        return `Confirma exclusão da solicitação de complemento do laudo ${this.cert.id} ?`
      },

      customerAddress() {

        let address = ""

        if (!this.cert || !this.cert.customer)
          return ""

        if (this.cert.customer.address)
          address += this.cert.customer.address.trim()  

        if (this.cert.customer.city) {
          if (address)
            address += " - "
          address += this.cert.customer.city.trim()  
        }

        if (this.cert.customer.state) {
          if (address)
            address += " / "
         address += this.cert.customer.state
        }

        if (!address)
          address = "Não cadastrado"

        return address
      },
    },

    methods: {

      onShowCustomerInvoice() {
        this.$router.push(`/financial/customer-invoices/${this.cert.customerInvoiceId}/show?historyBack=true`)
      },

      getAgeRange() {        
        return AnimalService.getAgeRange(this.cert.animal)
      },

      async onRemoveComplement() {
        
        this.removingComplement = true

        try {

          await this.service.removeComplement(this.cert.id)

          this.$reloadDataTables(["cert-complement-requests", "certs", "daily-certs"])

          this.$emit("onRemoveComplement")

          this.$msg.success("Solicitação removida com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)
        
        } finally {

          this.removingComplement = false
        }
      },
    },
  }

</script>